import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import clsx from 'clsx';
import { LoaderCircle } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'soft';
type Size = 'sm' | 'md' | 'lg';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

// Mirrors slightercrm/src/components/Button.tsx (minus framer-motion, which
// this app doesn't ship) so buttons look identical across both consoles.
const variants: Record<Variant, string> = {
  primary:
    'bg-gold-gradient text-on-primary shadow-soft hover:shadow-glow ' +
    'after:absolute after:inset-0 after:rounded-[inherit] after:bg-shine after:bg-[length:200%_100%] after:bg-[position:200%_0] hover:after:bg-[position:-200%_0] after:transition-[background-position] after:duration-700 after:opacity-0 hover:after:opacity-100',
  secondary: 'bg-surface border border-border-strong text-text-primary hover:bg-surface-alt hover:border-primary',
  ghost: 'bg-transparent text-text-secondary hover:bg-surface-alt hover:text-text-primary',
  danger: 'bg-danger text-white hover:opacity-90 shadow-soft',
  soft: 'bg-primary-soft text-primary hover:brightness-110',
};

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-5 text-[15px] gap-2',
};

export const Button = forwardRef<HTMLButtonElement, Props>(
  ({ variant = 'primary', size = 'md', loading, disabled, className, children, iconLeft, iconRight, ...rest }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={clsx(
        'relative inline-flex items-center justify-center overflow-hidden rounded-lg font-semibold tracking-tight',
        'transition-all duration-200 ease-out active:translate-y-px',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        sizes[size],
        variants[variant],
        className,
      )}
      {...rest}
    >
      <span className="relative z-10 inline-flex items-center justify-center gap-[inherit]">
        {loading ? <LoaderCircle size={size === 'sm' ? 14 : 16} className="animate-spin" /> : iconLeft}
        {children}
        {!loading && iconRight}
      </span>
    </button>
  ),
);
Button.displayName = 'Button';
